import React, { useMemo, useState } from 'react';
import ClayLayout from '@clayui/layout';
import ClayForm, { ClayInput } from '@clayui/form';
import FormComponent from '../../generic-components/formComponent/FormComponent';
import { useForm } from 'react-hook-form';
import './LogInForm.css';
import { tradeLicenseFields, membershipNumberFields, userLoginInfoFields } from './LoginConfig';
import CutButton from '../../generic-components/customCutButton/CutButton';
import { getBidSpringOAuthClient } from '../../apis/apiClient';
import { useApiMutation } from '../../apis/useApi';
import { CustomerMembership } from '../MembershipForm/CustomerMembershipResponse';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { renderLabelWithAsterisk } from '../../utils/commanUtil';
import { LANGUAGE } from '../../utils/languageProperties';

interface SiebelCompanyResponse {
    companyName: string;
    dcMembershipExpiryDate: string;
    dcMembershipNumber: number;
    tradeLicenseNumber: number;
}

interface VerifyApiResponse {
    success: boolean;
    data: SiebelCompanyResponse[];
    error?: any;
}

type VerificationErrorType = '' | 'invalid-information' | 'record-not-found';

const LoginForm = () => {
    const {
        control,
        register,
        watch,
        trigger,
        handleSubmit,
        setError,
        setValue,
        clearErrors,
        formState: { errors }
    } = useForm({ mode: 'all' });

    const navigate = useNavigate();
    const { login } = useAuth();
    const {
        categoryId,
        taxonomyCategoryIds,
        taxonomySubCategoryIds,
        partnerId,
        parterToOfferId
    } = useParams<{
        categoryId: string;
        taxonomyCategoryIds: string;
        taxonomySubCategoryIds: string;
        partnerId: string;
        parterToOfferId: string;
    }>();

    const [loginMode, setLoginMode] = useState<'tradeLicense' | 'membershipNumber'>('tradeLicense');

    const handleModeChange = (mode: 'tradeLicense' | 'membershipNumber') => {
        setLoginMode(mode);
        setValue('tradeLicenseNumber', '');
        setValue('membershipNumber', '');
        setValue('expiryDate', '');
        clearErrors(['tradeLicenseNumber', 'membershipNumber', 'expiryDate']);
        setIsVerified(false);
        setVerificationStatus('');
        setVerificationMessage('');
    };

    const currentFormFields = loginMode === 'tradeLicense' ? tradeLicenseFields : membershipNumberFields;

    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState<
        '' | 'success' | 'error'
    >('');
    const [verificationMessage, setVerificationMessage] = useState('');
    const [verificationErrorType, setVerificationErrorType] =
        useState<VerificationErrorType>('');
    const [matchedMembershipDetails, setMatchedMembershipDetails] =
        useState<SiebelCompanyResponse | null>(null);

    const scopeKey = 'BID Partner';

    const submitMemberShipForm = useApiMutation<CustomerMembership, any>(
        `/o/c/bidleads/scopes/${encodeURIComponent(scopeKey)}`,
        'post',
        {
            onSuccess: (data: CustomerMembership) => {
                if (data?.id) {
                    navigate(
                        `/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partnerId}/success-page/${parterToOfferId}`
                    );
                } else {
                    navigate(
                        `/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partnerId}/error-page/${parterToOfferId}`
                    );
                }
            },
            onError: () => {
                navigate(
                    `/categories/${categoryId}/${taxonomyCategoryIds}/${taxonomySubCategoryIds}/${partnerId}/error-page/${parterToOfferId}`
                );
            }
        }
    );

    const normalizeExpiry = (value?: string | null) => {
        if (!value) return '';

        const parts = value.split('-');
        if (parts.length === 3) {
            const [year, month, day] = parts;
            const mm = month?.padStart(2, '0');
            const dd = day?.padStart(2, '0');
            return `${dd}/${mm}/${year}`;
        }

        return value;
    };

    const tradeLicense = watch('tradeLicenseNumber');
    const membershipNumberValue = watch('membershipNumber');
    const expiryDate = watch('expiryDate');

    const verifyAPI = async (valueToVerify: string, mode: 'tradeLicense' | 'membershipNumber') => {
        const client = await getBidSpringOAuthClient();

        const basePath = mode === 'tradeLicense'
            ? '/api/v1/siebel/trade-license-details/verify'
            : '/api/v1/siebel/membership-details/verify';

        const url = `${basePath}?p_auth=${(window as any)?.Liferay?.authToken}`;

        const queryParams = new URLSearchParams({
            number: String(valueToVerify).trim(),
            expiryDate: expiryDate
        }).toString();

        const response = await client.fetch(`${url}&${queryParams}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                jsessionid: (
                    window as any
                )?.Liferay?.ThemeDisplay?.getSessionId?.()
            }
        });

        return response as VerifyApiResponse;
    };

    const handleVerificationError = (apiMessage?: string) => {
        const normalizedMessage = (apiMessage || '').trim().toLowerCase();

        setVerificationStatus('error');
        setIsVerified(false);
        setMatchedMembershipDetails(null);
        setValue('companyName', '');
        clearErrors('companyName');

        const typeLabel = loginMode === 'tradeLicense' ? 'Trade License Number' : 'Membership Number';
        const typeLabelLower = loginMode === 'tradeLicense' ? 'trade license' : 'membership';

        if (normalizedMessage === 'record not found.') {
            setVerificationErrorType('record-not-found');
            setVerificationMessage(
                `We couldn’t find Member based on ${typeLabel} you provided. Please apply to become a Member`
            );
            return;
        }

        if (normalizedMessage === 'invalid information.') {
            setVerificationErrorType('invalid-information');
            setVerificationMessage(
                'There seems to be a problem with the information you provided. Please contact'
            );
            return;
        }

        setVerificationErrorType('invalid-information');
        setVerificationMessage(`Unable to verify ${typeLabelLower} details.`);
    };

    const handleVerify = async () => {
        const fieldToValidate = loginMode === 'tradeLicense' ? 'tradeLicenseNumber' : 'membershipNumber';
        const valid = await trigger([fieldToValidate, 'expiryDate']);
        if (!valid) {
            return;
        }

        setIsVerifying(true);
        setVerificationMessage('');
        setVerificationStatus('');
        setVerificationErrorType('');
        setIsVerified(false);
        setMatchedMembershipDetails(null);

        try {
            const valueToVerify = loginMode === 'tradeLicense' ? tradeLicense : membershipNumberValue;
            const response = await verifyAPI(valueToVerify, loginMode);
            if (!response?.success) {
                handleVerificationError(response?.error?.message);
                return;
            }
            const matchedCompany = Array.isArray(response?.data)
                ? response.data[0]
                : null;

            if (matchedCompany) {
                setValue('companyName', matchedCompany.companyName);
                clearErrors('companyName');
                setIsVerified(true);
                setMatchedMembershipDetails(matchedCompany);
                setVerificationStatus('success');
                setVerificationErrorType('');
                setVerificationMessage(
                    `${loginMode === 'tradeLicense' ? 'Trade License' : 'Membership Number'} has been verified.`
                );
                return;
            }

            handleVerificationError(
                response?.error?.message || 'Invalid information'
            );
        } catch (error) {
            handleVerificationError((error as Error)?.message);
        } finally {
            setIsVerifying(false);
        }
    };

    const isArabic =
        String((window as any)?.Liferay.ThemeDisplay.getLanguageId()) ===
        'ar_SA';

    const onSubmit = (data: any, event: any) => {
        event?.preventDefault();
        event?.stopPropagation();

        if (!isVerified) {
            setVerificationStatus('error');
            setVerificationErrorType('');
            setVerificationMessage(
                `Please verify ${loginMode === 'tradeLicense' ? 'trade license' : 'membership number'} and expiry date first.`
            );
            return;
        }

        login();

        const payload = {
            r_offerToLead_c_partnerOfferId: parterToOfferId,
            companyName: data?.companyName,
            customerName:
                `${data?.firstName || ''} ${data?.lastName || ''}`.trim(),
            membershipNumber: String(
                matchedMembershipDetails?.dcMembershipNumber || ''
            ),
            phoneNumber: data?.phoneNumber,
            email: data?.email,
            designation: data?.designation,
            tradeLicenseNumber: String(
                matchedMembershipDetails?.tradeLicenseNumber || ''
            ),
            membershipOrTradeLicenseExpiryDate: data?.expiryDate
        };

        submitMemberShipForm.mutate({
            data: payload,
            params: {}
        });
    };

    const visibleUserFields = useMemo(() => {
        return userLoginInfoFields;
    }, []);

    return (
        <div className="login-form">
            <h1
                className="login-form-title"
                style={{
                    color: '#FFFFFF',
                    fontSize: '26px',
                    marginBottom: '8px',
                    direction: isArabic ? 'rtl' : 'ltr'
                }}
            >
                {LANGUAGE['membership-login-form-heading']}
            </h1>
            <p
                className="login-form-text"
                style={{
                    color: '#FFFFFF',
                    fontSize: '16px',
                    marginBottom: '40px',
                    direction: isArabic ? 'rtl' : 'ltr'
                }}
            >
                {LANGUAGE['membership-login-form-subheading']}
            </p>
            {/* <p
                className="login-form-text"
                style={{
                    color: '#FFFFFF',
                    fontSize: '16px',
                    marginBottom: '3rem'
                }}
            >
                Note: In case you don't remember your membership number please
                contact 'customercare@dubaichamber.com'.
            </p> */}
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                {!isVerified && (
                    <ClayLayout.Row justify="start" className="content-gap" style={{ marginBottom: '20px' }}>
                        <ClayLayout.Col xl={12} lg={12} md={12} sm={12} xs={12}>
                            <div style={{ display: 'flex', gap: '20px', color: 'white' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="loginMode"
                                        value="tradeLicense"
                                        checked={loginMode === 'tradeLicense'}
                                        onChange={() => handleModeChange('tradeLicense')}
                                        disabled={isVerified}
                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                    />
                                    Trade License
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="loginMode"
                                        value="membershipNumber"
                                        checked={loginMode === 'membershipNumber'}
                                        onChange={() => handleModeChange('membershipNumber')}
                                        disabled={isVerified}
                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                    />
                                    Membership Number
                                </label>
                            </div>
                        </ClayLayout.Col>
                    </ClayLayout.Row>
                )}

                <ClayLayout.Row justify="start" className="content-gap">
                    {currentFormFields
                        ?.map((data) => (
                            <ClayLayout.Col
                                key={data.name}
                                xl={6}
                                lg={6}
                                md={6}
                                sm={6}
                                xs={12}
                            >
                                <FormComponent
                                    isPartnerType={true}
                                    placeholder={data.placeholder}
                                    label={data.label}
                                    name={data.name}
                                    register={register}
                                    type={data.type}
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    errorTitle={data.errorMessage}
                                    required={data.required}
                                    component={
                                        data.component as
                                        | 'input'
                                        | 'textarea'
                                        | undefined
                                    }
                                    disabled={isVerified}
                                />
                            </ClayLayout.Col>
                        ))}
                </ClayLayout.Row>

                {!!verificationMessage && (
                    <p
                        className={`verify-message ${verificationStatus === 'success'
                            ? 'verify-success'
                            : 'verify-error'
                            }`}
                    >
                        {verificationMessage}{' '}
                        {verificationErrorType === 'invalid-information' && (
                            <a
                                href="mailto:customercare@dubaichamber.com"
                                className="verify-message-inline-link verify-message-email-link"
                            >
                                customercare@dubaichamber.com
                            </a>
                        )}
                        {verificationErrorType === 'record-not-found' && (
                            <>
                                <a
                                    href="https://www.dubaichambers.com/en/services?category=271644"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="verify-message-inline-link verify-message-cta-link"
                                >
                                    Click here
                                </a>
                            </>
                        )}
                    </p>
                )}

                {!isVerified && (
                    <div className="button_container mt-6 d-flex justify-content-center full-width-button verify-button-wrapper">
                        <CutButton
                            title={isVerifying ? 'Verifying...' : 'Verify'}
                            buttonType="button"
                            disabled={isVerifying}
                            onClick={handleVerify}
                        />
                    </div>
                )}

                {isVerified && (
                    <>
                        <ClayLayout.Row justify="start" className="content-gap">
                            {visibleUserFields?.map((data) => (
                                <ClayLayout.Col
                                    key={data.name}
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={6}
                                    xs={12}
                                >
                                    <FormComponent
                                        isPartnerType={true}
                                        placeholder={data.placeholder}
                                        label={data.label}
                                        name={data.name}
                                        register={register}
                                        type={data.type}
                                        control={control}
                                        errors={errors}
                                        watch={watch}
                                        setError={setError}
                                        clearErrors={clearErrors}
                                        errorTitle={data.errorMessage}
                                        required={data.required}
                                        component={
                                            data.component as
                                            | 'input'
                                            | 'textarea'
                                            | undefined
                                        }
                                        disabled={data.name === 'companyName'}
                                    />
                                </ClayLayout.Col>
                            ))}
                        </ClayLayout.Row>

                        <div className="button_container mt-6 d-flex justify-content-center full-width-button">
                            <CutButton
                                title="Submit"
                                buttonType="submit"
                                disabled={submitMemberShipForm.isLoading}
                            />
                        </div>
                        <div
                            className="mt-5"
                            style={{
                                color: 'white',
                                fontSize: '16px',
                                textAlign: 'center'
                            }}
                        >
                            By submitting this form, you acknowledge that the
                            information, personal data and documents provided
                            are correct, and you consent to share them with
                            relevant third parties to use and facilitate
                            collaborations and partnerships for the purposes of
                            the program
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default LoginForm;
